import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  Input,
  OnInit,
  Signal,
  Type,
  signal,
  computed,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { HpDialogButton, HpDialogRef } from './hp-dialog.types';

interface ProcessedButton {
  label: string;
  variant: 'primary' | 'accent' | 'ghost' | 'danger';
  class: string;
  disabled: Signal<boolean>;
  onClick: () => void;
}

const BUTTON_VARIANT_CLASSES: Record<ProcessedButton['variant'], string> = {
  primary: 'btn-variant-primary bg-hp-ink text-hp-paper hover:opacity-90',
  accent: 'btn-variant-accent bg-hp-accent text-white hover:opacity-90',
  ghost: 'btn-variant-ghost bg-transparent border text-hp-ink2 hover:bg-hp-panel-deep',
  danger: 'btn-variant-danger bg-transparent border border-hp-crit/40 text-hp-crit hover:bg-hp-crit/10',
};

@Component({
  selector: 'hp-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule],
  styles: `
    :host {
      position: fixed;
      inset: 0;
      z-index: 9999;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .btn-variant-ghost {
      border-color: var(--hp-hair);
    }
    :host-context(html.dark) .btn-variant-primary {
      background-color: #293445;
      color: #E8ECF3;
    }
  `,
  template: `
    <!-- Backdrop -->
    <div
      class="absolute inset-0"
      style="background: rgba(20,18,14,0.45); backdrop-filter: blur(2px);"
      (click)="onBackdropClick()"
    ></div>

    <!-- Dialog card -->
    <div
      class="relative flex flex-col bg-hp-paper rounded-xl border overflow-hidden"
      style="border-color: var(--hp-hair); box-shadow: 0 20px 60px rgba(20,18,14,0.22), 0 0 0 1px rgba(255,255,255,0.06); max-height: 85vh;"
      [style.width]="width"
      [style.height]="height"
      (click)="$event.stopPropagation()"
    >

      <!-- Header -->
      <div class="flex-shrink-0 flex items-center justify-between px-5 py-4 border-b" style="border-color: var(--hp-hair);">
        <span class="text-[15px] font-semibold text-hp-ink tracking-[-0.2px]">{{ title }}</span>
        @if (closable) {
          <button
            class="flex items-center justify-center w-7 h-7 rounded-md text-hp-muted hover:bg-hp-panel-deep hover:text-hp-ink transition-colors"
            (click)="dialogRef.close()"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 stroke-width="2.5" stroke-linecap="round">
              <path d="M18 6 6 18M6 6l12 12"/>
            </svg>
          </button>
        }
      </div>

      <!-- Dynamic content -->
      <div class="flex-1 min-h-0 overflow-y-auto">
        <ng-container
          [ngComponentOutlet]="contentComponent"
          [ngComponentOutletInputs]="contentInputs"
        />
      </div>

      <!-- Footer -->
      @if (processedButtons.length > 0) {
        <div class="flex-shrink-0 flex items-center justify-end gap-2 px-5 py-4 border-t" style="border-color: var(--hp-hair);">
          @for (btn of processedButtons; track btn.label) {
            <button
              class="flex items-center h-8 px-4 rounded-md font-hp-sans text-[10.5px] font-semibold tracking-[0.6px] uppercase transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              [ngClass]="btn.class"
              [disabled]="btn.disabled()"
              (click)="btn.onClick()"
            >{{ btn.label }}</button>
          }
        </div>
      }

    </div>
  `,
})
export class HpDialogComponent implements OnInit {
  @Input() title = '';
  @Input() width = '520px';
  @Input() height: string | null = null;
  @Input() closable = true;
  @Input() dialogRef!: HpDialogRef;
  @Input() contentComponent!: Type<unknown>;
  @Input() contentInputs: Record<string, any> = {};
  @Input() buttons: HpDialogButton[] = [];

  processedButtons: ProcessedButton[] = [];

  ngOnInit(): void {
    this.processedButtons = this.buttons.map((btn) => {
      const variant = btn.variant ?? 'ghost';
      return {
        label: btn.label,
        variant,
        class: BUTTON_VARIANT_CLASSES[variant],
        disabled:
          typeof btn.disabled === 'function'
            ? (btn.disabled as Signal<boolean>)
            : signal(!!btn.disabled),
        onClick: () => btn.onClick(this.dialogRef),
      };
    });
  }

  onBackdropClick(): void {
    if (this.closable) this.dialogRef.close();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.closable) this.dialogRef.close();
  }
}
